import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICliente, ILuminaria } from 'modelos/src';
import { AuxiliaresModule } from '../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../login/login.service';
import { LuminariasHttpService } from '../../../auxiliares/servicios/http/luminarias.service';
import { ActivosService } from '../../../auxiliares/servicios/http/activos.service';
import { UbicacionService } from '../../../auxiliares/servicios/http/ubicacion.service';
import { AlarmasDomiciliariasService } from '../../entidades/alarmas/alarmas-domiciliarias/alarmas-domiciliarias.service';
import { DispositivosLorawanService } from '../../entidades/administracion/dispositivos-lorawan/dispositivos-lorawan.service';
import { JerarquiaClienteComponent } from '../jerarquia-cliente/jerarquia-cliente.component';
import { InfoBannerComponent } from '../../../componentes/info-banner/info-banner.component';
import { CardHeaderComponent } from '../../../componentes/card-header/card-header.component';
import { escaparHtml } from '../../../auxiliares/html-seguro.util';

export interface ICambiarClienteData {
  entidad: EntidadCambioCliente;
  tipoEntidad: string;
  // Sólo 'Dispositivo Lorawan': luminaria a la que está asignado el nodo.
  luminaria?: ILuminaria;
}

export type EntidadCambioCliente =
  | 'Luminaria'
  | 'Alarma'
  | 'Activo'
  | 'Vehículo'
  | 'Zona'
  | 'Dispositivo Lorawan';

@Component({
  imports: [
    AuxiliaresModule,
    JerarquiaClienteComponent,
    InfoBannerComponent,
    CardHeaderComponent,
  ],
  selector: 'app-cambiar-cliente',
  templateUrl: './cambiar-cliente.component.html',
  styleUrls: ['./cambiar-cliente.component.scss'],
})
export class CambiarClienteComponent implements OnInit {
  dialogData = inject<ICambiarClienteData>(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject<MatDialogRef<CambiarClienteComponent>>(MatDialogRef, {
    optional: true,
  });
  private luminariasService = inject(LuminariasHttpService);
  private activosService = inject(ActivosService);
  private alarmasService = inject(AlarmasDomiciliariasService);
  private ubicacionService = inject(UbicacionService);
  private dispositivosLorawanService = inject(DispositivosLorawanService);
  helper = inject(HelperService);

  public loading = false;
  public miCliente = LoginService.getCliente();
  public clienteElegido: ICliente = null;
  public idClienteElegido: string | null = null;
  public height = 'unset';
  public maxHeight = 'unset';

  public entidad: any;
  public tipoEntidad: string;
  public nombreEntidad?: string;
  public moverTracker = true;
  public moverCamaras = true;
  public luminaria?: ILuminaria;

  get idCliente() {
    return this.clienteElegido?._id;
  }

  get detalleCambio(): string {
    switch (this.tipoEntidad) {
      case 'Luminaria':
        return `
          <ul style="margin: 3px 0 0 0; padding-left: 20px;">
            <li>La <strong>luminaria</strong> y <strong>su dispositivo LoRaWAN</strong> asociado se reasignarán al nuevo cliente.</li>
            <li>Se desasignarán los <strong>perfiles y grupos</strong> actuales de la luminaria, ya que pertenecen al cliente actual.</li>
            <li>Se desasignarán los <strong>modelos</strong> actuales de la luminaria y el dispositivo LoRaWAN, excepto si se tratan de modelos globales.</li>
            <li>Los perfiles, grupos y modelos <strong>no se eliminarán del cliente actual</strong>, sólo se desasignarán en la luminaria.</li>
            <li>Se eliminarán las <strong> configuraciones de eventos personalizados </strong> asociados a la luminaria en el cliente actual.</li>
            <li>Se eliminarán las <strong> notas y contactos </strong> de la luminaria.</li>
            <li>Se migrará el historial completo: <strong>eventos generados, reportes, consumos y comandos enviados</strong>.</li>
            <li>Se registrará el <strong>cambio de cliente</strong> y este podrá visualizarse en la sección de <strong>Asignaciones</strong> dentro de los detalles de la luminaria.</li>
          </ul>`;
      case 'Activo':
        return `
          <ul style="margin: 3px 0 0 0; padding-left: 20px;">
            <li>El <strong>activo</strong> se reasignará al nuevo cliente.</li>
            <li>Se desasignarán el <strong>grupo</strong> del activo.</li>
            <li>Se limpiará el <strong>modo desactivado</strong> y las <strong>configuraciones de horarios de atención, así como los clientes que pueden atender eventos y servicios ténicos</strong>.</li>
            <li>Se eliminarán las <strong>configuraciones de eventos personalizados</strong>, los <strong>certificados</strong>, las <strong>notas</strong>, los <strong>contactos</strong> del activo.</li> 
            <li>No se migrarán ni los <strong>eventos</strong> ni los <strong>reportes</strong> asociados al activo.</li>
            ${
              this.moverTracker
                ? `<li>El <strong>tracker</strong> asociado se moverá al nuevo cliente: se restauraran sus horarios de atención, se limpiarán </strong>modelo<strong> y <strong>servicios</strong> no globales, y se eliminarán <strong>configuraciones de reenvíos, historial de estados, certificados y comandos</strong> del tracker.</li>`
                : `<li>El <strong>tracker</strong> se desvinculará del vehículo y permanecerá en el cliente anterior.</li>`
            }
            <li>Se registrará el <strong>cambio de cliente</strong> y este podrá visualizarse en la sección de <strong>Asignaciones</strong>.</li>
          </ul>`;
      case 'Vehículo':
        return `
          <ul style="margin: 3px 0 0 0; padding-left: 20px;">
            <li>El <strong>vehículo</strong> se reasignará al nuevo cliente.</li>
            <li>Se desasignarán la <strong>flota</strong>, el <strong>chofer</strong> y los <strong>recorridos</strong> del vehículo.</li>
            <li>Se limpiará el <strong>estado de cuenta</strong>, el <strong>modo desactivado</strong> y las <strong>configuraciones de horarios de atención, así como los clientes que pueden atender eventos y servicios ténicos</strong>.</li>
            <li>Se eliminarán las <strong>configuraciones de eventos personalizados</strong>, los <strong>recordatorios</strong>, los <strong>certificados</strong>, las <strong>documentaciones</strong>, las <strong>notas</strong>, los <strong>contactos</strong> y <strong>servicios/mantenimientos</strong> del vehículo.</li> 
            <li>Los <strong>envíos de vehículos</strong> se migrarán al nuevo cliente.</li>
            <li>No se migrarán ni los <strong>eventos</strong> ni los <strong>reportes</strong> asociados al vehículo.</li>
            <li>Se registrará el <strong>cambio de cliente</strong> y este podrá visualizarse en la sección de <strong>Asignaciones</strong>.</li>
            ${
              this.moverTracker
                ? `<li>El <strong>tracker</strong> asociado se moverá al nuevo cliente: se restauraran sus horarios de atención, se limpiarán </strong>modelo<strong> y <strong>servicios</strong> no globales, y se eliminarán <strong>configuraciones de reenvíos, historial de estados, certificados y comandos</strong> del tracker.</li>`
                : `<li>El <strong>tracker</strong> se desvinculará del vehículo y permanecerá en el cliente anterior.</li>`
            }
          </ul>`;
      case 'Zona':
        return `
          <ul style="margin: 3px 0 0 0; padding-left: 20px;">
            <li>La <strong>zona</strong> se reasignará al nuevo cliente.</li>
            <li>Se eliminarán las <strong>configuraciones de eventos personalizados</strong> que usan la zona como condición (entradas y salidas), porque pertenecen al cliente actual.</li>
            <li>La zona se <strong>quitará de los recorridos</strong> que la usan como parada.</li>
            <li>No se migran los <strong>eventos</strong> ni los <strong>reportes</strong> generados con la zona.</li>
          </ul>`;
      case 'Alarma':
        return `
          <ul style="margin: 3px 0 0 0; padding-left: 20px;">
            <li>La <strong>alarma</strong> se reasignará al nuevo cliente.</li>
            <li>Se limpiarán el <strong>estado de cuenta</strong>, el <strong>modo desactivado</strong> y las <strong>configuraciones de horarios de atención, así como los clientes que pueden atender eventos y servicios técnicos</strong>.</li>
            <li>El <strong>modelo</strong> de la alarma y el <strong>comunicador</strong> se conservarán solo si son globales; de lo contrario, se desasignarán.</li>
            <li>Los <strong>servicios contratados</strong> no globales se desasignarán de la alarma.</li>
            <li>Se eliminarán las <strong>configuraciones de eventos personalizados</strong>, <strong>notas y contactos</strong>, <strong>historial de estados</strong>, <strong>configuraciones de reenvío</strong> y <strong>excepciones</strong> de la alarma.</li>
            <li>El <strong>domicilio</strong> asociada se actualizará al nuevo cliente.</li>
            <li>Se registrará el <strong>cambio de cliente</strong> y este podrá visualizarse en la sección de <strong>Asignaciones</strong>.</li>
             ${
               this.moverCamaras
                 ? `<li>Las <strong>cámaras</strong> asociadas se moverán al nuevo cliente. Si el modelo de una cámara no es global, se desasignará.</li>`
                 : `<li>Las <strong>cámaras</strong> se desvincularán de la alarma y permanecerán en el cliente anterior.</li>`
             }
          </ul>`;
      case 'Dispositivo Lorawan': {
        const lum = this.luminaria;
        const nombreLum = lum?.identificacion || lum?.deveui || '';
        let itemLuminaria = `<li>El dispositivo no está asignado a ninguna luminaria.</li>`;
        if (lum) {
          itemLuminaria =
            this.idCliente && this.idCliente === lum.idCliente
              ? `<li>Quedará en el mismo cliente que su luminaria <strong>${escaparHtml(nombreLum)}</strong>, que conserva la asignación y su perfil.</li>`
              : `<li>Está asignado a la luminaria <strong>${escaparHtml(nombreLum)}</strong>, que pertenece a otro cliente: se <strong>desasignará</strong> y la luminaria quedará sin dispositivo. También se eliminará la <strong>configuración deseada</strong> del dispositivo.</li>`;
        }
        return `
          <ul style="margin: 3px 0 0 0; padding-left: 20px;">
            <li>Sólo el <strong>dispositivo LoRaWAN</strong> se reasignará al nuevo cliente. Para mover también la luminaria y su historial, usá <strong>Cambiar cliente</strong> desde la luminaria.</li>
            <li>Se desasignará el <strong>modelo</strong> del dispositivo, excepto si es global.</li>
            ${itemLuminaria}
            <li>No se migran los <strong>comandos</strong>, <strong>reportes</strong> ni <strong>eventos</strong>: quedan en el cliente actual.</li>
            <li>Se registrará el <strong>cambio de cliente</strong> en las <strong>Asignaciones</strong>.</li>
          </ul>`;
      }
      default:
        return `Al cambiar el cliente de esta ${this.tipoEntidad?.toLowerCase()}, se migrará toda la información asociada al nuevo cliente.
          <strong>Esta acción se realizará de manera inmediata.</strong>`;
    }
  }

  public volver() {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    } else {
      HelperService.volver();
    }
  }

  public async submit(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    if (!this.clienteElegido) {
      this.helper.notifError('No hay un cliente destino elegido');
      this.loading = false;
      return;
    }
    try {
      if (this.entidad.idCliente !== this.clienteElegido._id) {
        switch (this.tipoEntidad) {
          case 'Luminaria':
            await this.luminariasService.cambiarCliente(
              this.entidad._id,
              this.entidad.idCliente,
              this.clienteElegido._id,
            );
            break;
          case 'Activo':
          case 'Vehículo':
            await this.activosService.cambiarCliente(
              this.entidad._id,
              this.entidad.idCliente,
              this.clienteElegido._id,
              this.moverTracker,
            );
            break;
          case 'Zona':
            await this.ubicacionService.cambiarCliente(
              this.entidad._id,
              this.entidad.idCliente,
              this.clienteElegido._id,
            );
            break;
          case 'Alarma':
            await this.alarmasService.cambiarCliente(
              this.entidad._id,
              this.entidad.idCliente,
              this.clienteElegido._id,
              this.moverCamaras,
            );
            break;
          case 'Dispositivo Lorawan':
            await this.dispositivosLorawanService.cambiarCliente(
              this.entidad._id,
              this.clienteElegido._id,
            );
            break;
          default:
            this.helper.notifError(
              `Tipo de entidad no soportado: ${this.tipoEntidad}`,
            );
            this.loading = false;
            return;
        }
        this.helper.notifSuccess('Cliente cambiado con éxito');
        this.volver();
      }
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  ngOnInit(): void {
    if (this.dialogData) {
      this.entidad = this.dialogData.entidad;
      this.tipoEntidad = this.dialogData.tipoEntidad;
      this.luminaria = this.dialogData.luminaria;
    }
    this.nombreEntidad =
      this.entidad?.nombre ||
      this.entidad?.identificacion ||
      this.entidad?.name ||
      '';
    this.clienteElegido = this.entidad?.cliente;
    this.idClienteElegido = this.entidad?.cliente?._id || null;
    this.moverTracker = !!this.entidad?.idTracker;
    this.moverCamaras = !!this.entidad?.idsCamaras?.length;
  }
}
